import { useRef, useEffect } from 'react'
import { Dispatch } from 'redux'
import { showSnackbar } from '@global/snackbar/snackbar.actions'

interface Args {
  dispatch: Dispatch<any>
  isPasting?: boolean
  dispatchState?: React.Dispatch<any>
}
interface HookReturn {
  setPasteStatus: (status: boolean) => void
  setCopyStatus: (status: boolean) => void
}

interface CustomEventDetails {
  status: boolean
}

/**
 * Manage copy / paste state change and notifications.
 * - Show snackbar when pasting is in progress.
 * - Notify redux store of pasting progress state change.
 * - Set event listener to manage state and notification externally (DataContainer)
 * @param args
 * @param args.isPasting Context API pasting event state
 * @param args.dispatch Redux store state dispatch
 * @param args.dispatchState Context API dispatch from any parent container.
 * @returns {HookReturn} Returns an event trigger function to set state and show snackBar.
 */
export const usePasteNotification = ({
  isPasting = false,
  dispatch,
  dispatchState,
}: Args): HookReturn => {
  const pastingRef = useRef<boolean>(isPasting)

  useEffect(() => {
    /**
     * Event listener callback for paste event.
     * @param event A Custom Event that recieves the status boolean detail.
     */
    function setPasteStatus(event: CustomEvent<CustomEventDetails>): void {
      const status = event?.detail?.status
      if (pastingRef?.current === status) return
      pastingRef.current = status

      dispatch(
        showSnackbar({
          message: 'Pasting... please wait.',
          type: 'LOADER',
          status: pastingRef?.current,
        }),
      )
      dispatchState?.({ type: 'setPasteStatus', payload: status })
    }

    /**
     * Event listener callback for copy event.
     * @param event A Custom Event that recieves the status boolean detail.
     */
    function setCopyStatus(event: CustomEvent<CustomEventDetails>): void {
      const status = event?.detail?.status

      dispatch(
        showSnackbar({
          message: 'Copied',
          type: 'SUCCESS',
          status,
          duration: 5000,
        }),
      )
    }

    window.addEventListener('kalo.pasteEvent', setPasteStatus as EventListener, false)
    window.addEventListener('kalo.copyEvent', setCopyStatus as EventListener, false)

    return () => {
      window.removeEventListener('kalo.pasteEvent', setPasteStatus as EventListener)
      window.removeEventListener('kalo.copyEvent', setCopyStatus as EventListener)
    }
  }, [isPasting, dispatch, dispatchState])

  /**
   * Trigger the Custom Event that cascades side-effects for copy and pasting.
   * - Passes the status detail for if pasting is in progress.
   * @param {boolean} status A pasting action is active.
   */
  function setPasteStatus(status: boolean): void {
    const pasteEvent = new CustomEvent<CustomEventDetails>('kalo.pasteEvent', {
      detail: { status },
    })
    window.dispatchEvent(pasteEvent)
  }

  /**
   * Trigger the Custom Event that cascades side-effects for copy and pasting.
   * - Passes the status detail for if pasting is in progress.
   * @param {boolean} status A pasting action is active.
   */
  function setCopyStatus(status: boolean): void {
    const copyEvent = new CustomEvent<CustomEventDetails>('kalo.copyEvent', {
      detail: { status },
    })
    window.dispatchEvent(copyEvent)
  }

  return {
    setPasteStatus,
    setCopyStatus,
  }
}
